import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Archive, CheckCircle2, Copy, Eye, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import Bibliothek from './Bibliothek';
import TagEditor from './TagEditor';
import Vorschau from './Vorschau';
import { fehlerText, planerApi, TAGESTYP, WOCHENTAGE } from './api';

/* Trainingsplan-Editor (W3.1, 29.10.2026) — CRM /planer und /coach › Programme. Ein Plan = ein Zyklus
   (trainingszyklen): Tage als Tabs, `+ Tag`, Tagestyp, Übungen per Drag & Drop aus der Bibliothek, Felder je Übung,
   Ablauf-Regeln, Vorschau für den Kunden, Duplizieren / Als Vorlage / Archivieren / Freigeben. Jede Änderung trägt
   die gelesene `version`; bei 409 lädt der Editor neu (nichts wird still überschrieben). */

const GELTUNG = [['woche', 'nur diese Woche'], ['phase', 'diese Phase'], ['zyklus', 'ganzer Plan']];

export default function TrainingsplanEditor({ client, basis, zid, onZurueck, onGeoeffnet }) {
  const api = useMemo(() => planerApi(client, basis), [client, basis]);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState('');
  const [woche, setWoche] = useState(null);
  const [tag, setTag] = useState(null);
  const [geltung, setGeltung] = useState('woche');
  const [gewaehlt, setGewaehlt] = useState(null);
  const [vorschau, setVorschau] = useState(false);
  const [busy, setBusy] = useState(false);

  const uebernehmen = useCallback((d) => {
    setPlan(d);
    setWoche((w) => w || d.aktuelle_woche || 1);
    setTag((t) => (t && d.einheiten.some((e) => e.key === t) ? t : d.einheiten[0]?.key));
  }, []);
  const lade = useCallback(() => api.plan(zid).then((r) => { uebernehmen(r.data); setErr(''); })
    .catch((e) => setErr(fehlerText(e, 'Plan nicht geladen'))), [api, zid, uebernehmen]);
  useEffect(() => { setPlan(null); setWoche(null); lade(); }, [lade]);

  const tun = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.wochen) uebernehmen(r.data);
      if (ok) toast.success(ok);
      return r;
    } catch (e) {
      toast.error(fehlerText(e, 'Nicht gespeichert'));
      if (e?.response?.status === 409) lade();
      return null;
    } finally { setBusy(false); }
  };

  if (err) return <div className="space-y-3"><Button variant="ghost" size="sm" onClick={onZurueck}><ArrowLeft className="h-4 w-4" /> Zurück</Button><p className="text-sm text-destructive" role="alert">{err}</p></div>;
  if (!plan) return <Skeleton className="h-96" />;

  const v = plan.version;
  const darf = !!plan.darf_aendern && plan.status !== 'archiviert';
  const w = plan.wochen[(woche || 1) - 1];
  const meta = plan.einheiten.find((e) => e.key === tag) || plan.einheiten[0];
  const block = (slot, werte, g = geltung) => tun(() => api.block(zid, { version: v, woche, einheit: meta.key, slot, werte, geltung: g }));
  const einfuegen = (slot, name, index) => {
    const b = w.einheiten[meta.key][slot] || {};
    if (slot === 'zubringer') {
      const liste = (b.uebungen || []).filter((x) => x !== name);
      liste.splice(index ?? liste.length, 0, name);
      return block(slot, { uebungen: liste });
    }
    return block(slot, { uebung: name });
  };
  const entfernen = (slot, name) => {
    const b = w.einheiten[meta.key][slot] || {};
    return slot === 'zubringer' ? block(slot, { uebungen: (b.uebungen || []).filter((x) => x !== name) }) : block(slot, { uebung: '' });
  };
  const ziehen = async ({ source, destination, draggableId }) => {
    if (!destination || !darf || !destination.droppableId.startsWith('slot:')) return;
    const ziel = destination.droppableId.slice(5);
    if (draggableId.startsWith('bib:')) { await einfuegen(ziel, draggableId.slice(4), destination.index); return; }
    const [, quelle, ...rest] = draggableId.split(':');
    const name = rest.join(':');
    if (quelle === ziel) {
      if (ziel === 'zubringer' && source.index !== destination.index) await einfuegen(ziel, name, destination.index);
      return;
    }
    // Rolle wechseln: aus dem alten Block heraus, in den neuen hinein (zwei Schritte, je mit neuer Version)
    const r = await entfernen(quelle, name);
    if (r) {
      const neu = r.data;
      const b = neu.wochen[woche - 1].einheiten[meta.key][ziel] || {};
      const werte = ziel === 'zubringer' ? { uebungen: [...(b.uebungen || []).filter((x) => x !== name).slice(0, destination.index), name, ...(b.uebungen || []).filter((x) => x !== name).slice(destination.index)] } : { uebung: name };
      await tun(() => api.block(zid, { version: neu.version, woche, einheit: meta.key, slot: ziel, werte, geltung }));
    }
  };
  const tagVerschieben = (richtung) => {
    const keys = plan.einheiten.map((e) => e.key);
    const i = keys.indexOf(meta.key); const j = i + richtung;
    if (j < 0 || j >= keys.length) return;
    [keys[i], keys[j]] = [keys[j], keys[i]];
    tun(() => api.reihenfolge(zid, v, keys));
  };
  const umbenennen = () => {
    const n = window.prompt('Name des Plans', plan.name);
    if (n && n.trim() && n.trim() !== plan.name) tun(() => api.name(zid, v, n.trim()), 'Umbenannt');
  };

  return (
    <div className="space-y-4" data-testid="trainingsplan-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13px] text-muted-foreground">Training · Programme</p>
          {onZurueck && <Button variant="ghost" size="sm" className="-ml-2 gap-1.5 text-muted-foreground" onClick={onZurueck}><ArrowLeft className="h-4 w-4" /> Zurück</Button>}
          <h2 className="flex flex-wrap items-center gap-2 text-[26px] font-extrabold tracking-tight">
            <button type="button" onClick={darf ? umbenennen : undefined} className="break-words text-left" title={darf ? 'Umbenennen' : undefined}>{plan.name}</button>
            <Badge variant="outline">{plan.status}</Badge>
            {plan.programm && <Badge variant="secondary">Programm · Phase {plan.programm.phase}/{plan.programm.phasen_gesamt}</Badge>}
          </h2>
          <p className="text-sm text-muted-foreground">
            {plan.personen?.length ? `Für ${plan.personen.map((p) => p.name).join(', ')}` : plan.kurs_ids?.length ? 'Kursplan' : 'ohne Person'}
            {plan.standard_fuer?.length ? ' · Standardplan' : ''} · {plan.wochen_anzahl || plan.wochen.length} Wochen ab {plan.startdatum}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="min-h-[44px] gap-1.5" onClick={() => setVorschau((x) => !x)} aria-pressed={vorschau}><Eye className="h-4 w-4" /> So sieht es der Kunde</Button>
          <Button variant="outline" className="min-h-[44px] gap-1.5" disabled={busy}
            onClick={async () => { const r = await tun(() => api.duplizieren(zid, {}), 'Kopie angelegt'); if (r && onGeoeffnet) onGeoeffnet(r.data.id); }}>
            <Copy className="h-4 w-4" /> Duplizieren</Button>
          <Button variant="outline" className="min-h-[44px] gap-1.5" disabled={busy}
            onClick={() => { const n = window.prompt('Name der Vorlage (ohne Personen und Notizen — bitte ohne Kundennamen)', ''); if (n && n.trim()) tun(() => api.alsVorlage(zid, { name: n }), 'Als Vorlage gespeichert'); }}>
            <Save className="h-4 w-4" /> Als Vorlage</Button>
          {darf && plan.status === 'entwurf' && <Button className="min-h-[44px] gap-1.5" disabled={busy} onClick={() => tun(() => api.freigeben(zid, v), 'Freigegeben')}><CheckCircle2 className="h-4 w-4" /> Freigeben</Button>}
          {darf && <Button variant="ghost" className="min-h-[44px] gap-1.5" disabled={busy}
            onClick={() => { if (window.confirm('Plan archivieren? Er ist danach schreibgeschützt.')) tun(() => api.archivieren(zid, v), 'Archiviert'); }}>
            <Archive className="h-4 w-4" /> Archivieren</Button>}
        </div>
      </div>

      {plan.luecken?.length > 0 && <p className="text-xs text-amber-600">Offen: {plan.luecken.slice(0, 4).join(' · ')}{plan.luecken.length > 4 ? ' …' : ''}</p>}

      <div className="flex flex-wrap items-end gap-3 rounded-[14px] border border-border bg-card px-5 py-[18px]">
        <label className="flex flex-col gap-0.5 text-xs">
          <span className="text-muted-foreground">Woche</span>
          <select aria-label="Woche" value={woche} onChange={(e) => setWoche(Number(e.target.value))} className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
            {plan.wochen.map((x) => <option key={x.woche} value={x.woche}>W{x.woche} · {x.typ === 'deload' ? 'Entlastung' : x.typ === 'belastung' ? 'Belastung' : x.typ}{x.woche === plan.aktuelle_woche ? ' · läuft' : ''}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-xs">
          <span className="text-muted-foreground">Änderungen gelten für</span>
          <select aria-label="Änderungen gelten für" value={geltung} onChange={(e) => setGeltung(e.target.value)} className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm" data-testid="plan-geltung">
            {GELTUNG.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-xs">
          <span className="text-muted-foreground">Tage</span>
          <select aria-label="Ablauf der Tage" disabled={!darf} value={plan.tage_ablauf || 'reihenfolge'} onChange={(e) => tun(() => api.ablauf(zid, { version: v, tage_ablauf: e.target.value }))}
            className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
            <option value="reihenfolge">der Reihe nach</option><option value="kunde_waehlt">Kunde wählt den Tag</option>
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-xs">
          <span className="text-muted-foreground">Übungen</span>
          <select aria-label="Ablauf der Übungen" disabled={!darf} value={plan.uebungs_ablauf || 'reihenfolge'} onChange={(e) => tun(() => api.ablauf(zid, { version: v, uebungs_ablauf: e.target.value }))}
            className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
            <option value="reihenfolge">deine Reihenfolge</option><option value="frei">frei</option>
          </select>
        </label>
      </div>

      {vorschau ? <Vorschau api={api} zid={zid} woche={woche} version={v} /> : (
        <DragDropContext onDragEnd={ziehen}>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-3 rounded-[14px] border border-border bg-card px-5 py-[18px]">
              <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Tage">
                {plan.einheiten.map((e) => (
                  <button key={e.key} type="button" role="tab" aria-selected={e.key === meta.key} onClick={() => setTag(e.key)} data-testid={`plan-tab-${e.key}`}
                    className={`min-h-[44px] shrink-0 rounded-full border px-4 text-sm font-bold ${e.key === meta.key ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground hover:bg-muted/70'}`}>
                    {e.titel || `Tag ${e.key}`}<span className="ml-1 text-[10px] opacity-80">{TAGESTYP[e.tagestyp || 'training']}</span>
                  </button>
                ))}
                {darf && (
                  <button type="button" onClick={() => tun(() => api.tagNeu(zid, { version: v }), 'Tag angelegt')} disabled={busy} data-testid="plan-tag-neu"
                    className="flex min-h-[44px] shrink-0 items-center gap-1 rounded-full border border-dashed border-border px-4 text-sm font-bold hover:bg-muted"><Plus className="h-4 w-4" /> Tag</button>
                )}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col gap-0.5 text-xs">
                  <span className="text-muted-foreground">Tagestyp</span>
                  <select aria-label="Tagestyp" disabled={!darf} value={meta.tagestyp || 'training'} data-testid="plan-tagestyp"
                    onChange={(e) => tun(() => api.tag(zid, meta.key, { version: v, tagestyp: e.target.value }))}
                    className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
                    {Object.entries(TAGESTYP).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
                <div className="flex flex-col gap-0.5 text-xs">
                  <span className="text-muted-foreground">Wochentage</span>
                  <div className="flex gap-1" role="group" aria-label="Wochentage">
                    {WOCHENTAGE.map((d, i) => {
                      const an = (meta.wochentage || []).includes(i);
                      return (
                        <button key={d} type="button" aria-pressed={an} disabled={!darf}
                          onClick={() => tun(() => api.tag(zid, meta.key, { version: v, wochentage: an ? meta.wochentage.filter((x) => x !== i) : [...(meta.wochentage || []), i] }))}
                          className={`h-11 w-11 rounded-full border text-xs font-bold ${an ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted'}`}>{d}</button>
                      );
                    })}
                  </div>
                </div>
                {darf && (
                  <div className="ml-auto flex gap-1">
                    <Button variant="outline" size="icon" className="h-11 w-11" aria-label="Tag nach links" onClick={() => tagVerschieben(-1)}><ArrowLeft className="h-4 w-4" /></Button>
                    <Button variant="outline" size="icon" className="h-11 w-11" aria-label="Tag nach rechts" onClick={() => tagVerschieben(1)}><ArrowRight className="h-4 w-4" /></Button>
                    <Button variant="outline" className="h-11 gap-1" onClick={() => { const n = window.prompt('Titel des Tages', meta.titel); if (n && n.trim()) tun(() => api.tag(zid, meta.key, { version: v, titel: n.trim() })); }}>Titel</Button>
                    {plan.einheiten.length > 1 && <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Tag löschen"
                      onClick={() => { if (window.confirm(`„${meta.titel}“ löschen?`)) tun(() => api.tagWeg(zid, meta.key, v), 'Tag gelöscht'); }}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                )}
              </div>
              <TagEditor plan={plan} woche={woche} tagKey={meta.key} darf={darf && !busy} gewaehlt={gewaehlt}
                onFeld={(slot, werte) => block(slot, werte)} onEntfernen={entfernen}
                onEinfuegen={async (slot) => { await einfuegen(slot, gewaehlt); setGewaehlt(null); }} />
            </div>
            <aside className="rounded-[14px] border border-border bg-card px-5 py-[18px] lg:sticky lg:top-4 lg:self-start">
              <Bibliothek api={api} gewaehlt={gewaehlt} onWahl={setGewaehlt} darf={darf} />
            </aside>
          </div>
        </DragDropContext>
      )}
      {darf && <p className="text-xs text-muted-foreground">Mainlift und Power 1 wechseln für den ganzen Plan, Zubringer und Power 2 je Phase — Sätze, Wdh, RPE, Gewicht, Tempo und Hinweise gelten für die Auswahl „gilt für“. Entlastungswochen behalten ihre Satzzahl.</p>}
    </div>
  );
}
